// Fragment "affiliate" — the practitioner's affiliate page (admin/affiliate/**).
//
// It REPLACES the `aff.*` block that used to sit in admin/base.js: those keys described the
// v0 program (float dollars, a hand-flipped "paid" status, one share link, a settings
// object the practitioner could read) and not one of them survives the v1 shapes. The move
// out of base.js is also what lets this whole lane be edited without touching the file
// every other admin screen shares.
//
// `aff.error.*` is the CLOSED refusal set of the module's API contract, member for member,
// plus `UNKNOWN` for a code a later backend may raise that this build predates. The
// server's own `message` is English and is never rendered — see admin/affiliate/errorText.js.
export default {
  fr: {
    "aff.title": "Programme d'affiliation",
    "aff.subtitle": "Parrainez d'autres praticiens et gagnez une part de ce que la plateforme encaisse sur eux.",
    "aff.loading": "Chargement…",

    "aff.banner.programDisabled": "Le programme d'affiliation est actuellement suspendu : les nouveaux parrainages ne rapportent rien tant qu'il ne reprend pas. Ce que vous avez déjà gagné reste acquis.",
    "aff.banner.accountDisabled": "Votre compte d'affilié est désactivé. Aucun nouveau parrainage ne sera comptabilisé ; contactez Solunary.",

    "aff.share.title": "Vos liens de parrainage",
    "aff.share.desc": "Une personne qui arrive par l'un de ces liens vous est attribuée pendant {days} jours.",
    "aff.share.signup": "Inscription praticien",
    "aff.share.marketplace": "Marketplace",
    "aff.share.copy": "Copier",
    "aff.share.copied": "Copié",
    "aff.share.codeLabel": "Votre code :",
    "aff.share.rates": "{saas} % de l'abonnement d'un praticien parrainé · {marketplace} % de la commission de la plateforme sur une vente parrainée. Taux indicatifs : chaque commission conserve le taux en vigueur au moment de l'encaissement.",

    "aff.code.title": "Changer votre code",
    "aff.code.hint": "Vous pouvez remplacer le code généré une seule fois. Les liens déjà partagés pointent vers le code actuel et cesseront de fonctionner.",
    "aff.code.placeholder": "moncode",
    "aff.code.submit": "Changer le code",
    "aff.code.pattern": "De 4 à 16 caractères : minuscules et chiffres.",
    "aff.code.saved": "Code modifié.",
    "aff.code.locked": "Votre code a déjà été changé une fois : il est désormais définitif.",
    "aff.code.disabled": "Le code ne peut pas être modifié tant que votre compte d'affilié est désactivé.",

    "aff.stats.title": "Vos résultats",
    "aff.stats.clicksTotal": "Clics",
    "aff.stats.clicks30d": "{count} sur 30 jours",
    "aff.stats.referrals": "Praticiens parrainés",
    "aff.stats.referralsBreakdown": "{linked} inscrits · {active} actifs · {expired} terminés",
    "aff.stats.accrued": "Gagné (brut)",
    "aff.stats.onHold": "En attente de libération",
    "aff.stats.released": "Libéré",
    "aff.stats.available": "Retirable aujourd\u2019hui",
    "aff.stats.withdrawn": "Déjà versé",
    "aff.stats.reversed": "Repris",
    "aff.stats.identity": "Libéré + en attente + déjà versé = gagné − repris. Le montant retirable aujourd\u2019hui ne dépasse jamais le libéré, ni le solde de votre portefeuille.",

    "aff.payout.title": "Comment vous êtes payé",
    "aff.payout.body": "Vos gains d'affiliation arrivent dans votre portefeuille Solunary et se retirent au même endroit que vos ventes. Il n'y a pas de versement séparé.",
    "aff.payout.walletCta": "Ouvrir mon portefeuille",
    "aff.payout.notReady": "Aucun retrait n'est possible tant que votre compte bancaire de versement n'est pas connecté.",
    "aff.payout.setupCta": "Configurer mon compte de versement",

    "aff.referrals.title": "Praticiens que vous avez parrainés",
    "aff.referrals.empty": "Aucun parrainage pour l'instant. Partagez votre lien d'inscription pour commencer.",
    "aff.referrals.filterAll": "Tous les statuts",
    "aff.referrals.colPractice": "Cabinet",
    "aff.referrals.colStatus": "Statut",
    "aff.referrals.colLinkedAt": "Inscrit le",
    "aff.referrals.colActivatedAt": "Première facture",
    "aff.referrals.colWindowEndsAt": "Fin de la période",
    "aff.referrals.colInvoices": "Factures",
    "aff.referrals.colEarned": "Gagné",

    "aff.referralState.linked": "Inscrit",
    "aff.referralState.active": "Actif",
    "aff.referralState.expired": "Période terminée",
    "aff.referralState.voided": "Annulé",

    "aff.commissions.title": "Commissions",
    "aff.commissions.baseHint": "La base est la commission encaissée par la plateforme sur cette vente, pas le montant de la vente.",
    "aff.commissions.empty": "Aucune commission pour l'instant.",
    "aff.commissions.filterAllKinds": "Toutes les origines",
    "aff.commissions.filterAllStates": "Tous les statuts",
    "aff.commissions.colCreatedAt": "Date",
    "aff.commissions.colKind": "Origine",
    "aff.commissions.colCounterparty": "Cabinet",
    "aff.commissions.colBase": "Base",
    "aff.commissions.colRate": "Taux",
    "aff.commissions.colAmount": "Montant",
    "aff.commissions.colReversed": "Repris",
    "aff.commissions.colStatus": "Statut",
    "aff.commissions.colHold": "Libéré le",
    "aff.commissions.ratePct": "{pct} %",

    "aff.kind.saas": "Abonnement",
    "aff.kind.marketplace": "Vente",

    "aff.state.accrued": "Acquise",
    "aff.state.partially_reversed": "Partiellement reprise",
    "aff.state.reversed": "Reprise",

    "aff.page.info": "{from}–{to} sur {total}",
    "aff.page.prev": "Précédent",
    "aff.page.next": "Suivant",

    "aff.error.AFFILIATE_PROGRAM_DISABLED": "Le programme d'affiliation est suspendu : cette action est indisponible pour le moment.",
    "aff.error.AFFILIATE_CODE_UNKNOWN": "Ce code d'affiliation n'existe pas.",
    "aff.error.AFFILIATE_CODE_TAKEN": "Ce code est déjà utilisé par un autre cabinet. Choisissez-en un autre.",
    "aff.error.AFFILIATE_CODE_INVALID": "Code invalide : de 4 à 16 caractères, minuscules et chiffres uniquement.",
    "aff.error.AFFILIATE_CODE_ALREADY_CHANGED": "Votre code a déjà été changé une fois : il ne peut plus l'être.",
    "aff.error.AFFILIATE_ACCOUNT_DISABLED": "Votre compte d'affilié est désactivé.",
    "aff.error.AFFILIATE_COMMISSION_NOT_FOUND": "Cette commission est introuvable.",
    "aff.error.AFFILIATE_COMMISSION_ALREADY_REVERSED": "Cette commission a déjà été entièrement reprise.",
    "aff.error.AFFILIATE_RULE_INVALID": "Ce barème n'est pas valide.",
    "aff.error.REAUTH_REQUIRED": "Vérification d'identité requise pour cette action.",
    "aff.error.RATE_LIMITED": "Trop de requêtes : réessayez dans un instant.",
    "aff.error.UNKNOWN": "Une erreur est survenue et nous ne pouvons pas en dire plus.",
  },
  en: {
    "aff.title": "Affiliate program",
    "aff.subtitle": "Refer other practitioners and earn a share of what the platform collects on them.",
    "aff.loading": "Loading…",

    "aff.banner.programDisabled": "The affiliate program is paused: new referrals earn nothing until it resumes. What you have already earned stays yours.",
    "aff.banner.accountDisabled": "Your affiliate account is disabled. No new referral will be counted — please contact Solunary.",

    "aff.share.title": "Your referral links",
    "aff.share.desc": "Someone who arrives through one of these links is attributed to you for {days} days.",
    "aff.share.signup": "Practitioner signup",
    "aff.share.marketplace": "Marketplace",
    "aff.share.copy": "Copy",
    "aff.share.copied": "Copied",
    "aff.share.codeLabel": "Your code:",
    "aff.share.rates": "{saas} % of a referred practitioner's subscription · {marketplace} % of the platform's commission on a referred sale. Indicative rates: every commission keeps the rate in force when it was collected.",

    "aff.code.title": "Change your code",
    "aff.code.hint": "You may replace the generated code once. Links you have already shared point at the current code and will stop working.",
    "aff.code.placeholder": "mycode",
    "aff.code.submit": "Change code",
    "aff.code.pattern": "4 to 16 characters: lowercase letters and digits.",
    "aff.code.saved": "Code changed.",
    "aff.code.locked": "Your code has already been changed once — it is now permanent.",
    "aff.code.disabled": "The code cannot be changed while your affiliate account is disabled.",

    "aff.stats.title": "Your results",
    "aff.stats.clicksTotal": "Clicks",
    "aff.stats.clicks30d": "{count} in the last 30 days",
    "aff.stats.referrals": "Practitioners referred",
    "aff.stats.referralsBreakdown": "{linked} signed up · {active} active · {expired} ended",
    "aff.stats.accrued": "Earned (gross)",
    "aff.stats.onHold": "On hold",
    "aff.stats.released": "Released",
    "aff.stats.available": "Withdrawable today",
    "aff.stats.withdrawn": "Already paid out",
    "aff.stats.reversed": "Given back",
    "aff.stats.identity": "Released + on hold + already paid out = earned − given back. What is withdrawable today never exceeds the released amount, nor your wallet balance.",

    "aff.payout.title": "How you get paid",
    "aff.payout.body": "Affiliate earnings land in your Solunary wallet and are withdrawn in the same place as your sales. There is no separate payout.",
    "aff.payout.walletCta": "Open my wallet",
    "aff.payout.notReady": "No withdrawal is possible until your payout bank account is connected.",
    "aff.payout.setupCta": "Set up my payout account",

    "aff.referrals.title": "Practitioners you referred",
    "aff.referrals.empty": "No referrals yet. Share your signup link to get started.",
    "aff.referrals.filterAll": "All statuses",
    "aff.referrals.colPractice": "Practice",
    "aff.referrals.colStatus": "Status",
    "aff.referrals.colLinkedAt": "Signed up",
    "aff.referrals.colActivatedAt": "First invoice",
    "aff.referrals.colWindowEndsAt": "Window ends",
    "aff.referrals.colInvoices": "Invoices",
    "aff.referrals.colEarned": "Earned",

    "aff.referralState.linked": "Signed up",
    "aff.referralState.active": "Active",
    "aff.referralState.expired": "Window ended",
    "aff.referralState.voided": "Withdrawn",

    "aff.commissions.title": "Commissions",
    "aff.commissions.baseHint": "The base is the commission the platform collected on that sale, not the amount of the sale.",
    "aff.commissions.empty": "No commissions yet.",
    "aff.commissions.filterAllKinds": "All sources",
    "aff.commissions.filterAllStates": "All statuses",
    "aff.commissions.colCreatedAt": "Date",
    "aff.commissions.colKind": "Source",
    "aff.commissions.colCounterparty": "Practice",
    "aff.commissions.colBase": "Base",
    "aff.commissions.colRate": "Rate",
    "aff.commissions.colAmount": "Amount",
    "aff.commissions.colReversed": "Given back",
    "aff.commissions.colStatus": "Status",
    "aff.commissions.colHold": "Released on",
    "aff.commissions.ratePct": "{pct} %",

    "aff.kind.saas": "Subscription",
    "aff.kind.marketplace": "Sale",

    "aff.state.accrued": "Earned",
    "aff.state.partially_reversed": "Partly given back",
    "aff.state.reversed": "Given back",

    "aff.page.info": "{from}–{to} of {total}",
    "aff.page.prev": "Previous",
    "aff.page.next": "Next",

    "aff.error.AFFILIATE_PROGRAM_DISABLED": "The affiliate program is paused: this action is unavailable for now.",
    "aff.error.AFFILIATE_CODE_UNKNOWN": "There is no affiliate account with this code.",
    "aff.error.AFFILIATE_CODE_TAKEN": "Another practice already holds this code. Choose a different one.",
    "aff.error.AFFILIATE_CODE_INVALID": "Invalid code: 4 to 16 characters, lowercase letters and digits only.",
    "aff.error.AFFILIATE_CODE_ALREADY_CHANGED": "Your code has already been changed once and cannot be changed again.",
    "aff.error.AFFILIATE_ACCOUNT_DISABLED": "Your affiliate account is disabled.",
    "aff.error.AFFILIATE_COMMISSION_NOT_FOUND": "This commission no longer exists.",
    "aff.error.AFFILIATE_COMMISSION_ALREADY_REVERSED": "This commission has already been fully reversed.",
    "aff.error.AFFILIATE_RULE_INVALID": "That rate rule is not valid.",
    "aff.error.REAUTH_REQUIRED": "This action needs an identity check.",
    "aff.error.RATE_LIMITED": "Too many requests — try again in a moment.",
    "aff.error.UNKNOWN": "Something went wrong and we cannot say what.",
  },
  es: {
    "aff.title": "Programa de afiliados",
    "aff.subtitle": "Recomienda a otros profesionales y gana una parte de lo que la plataforma cobra por ellos.",
    "aff.loading": "Cargando…",

    "aff.banner.programDisabled": "El programa de afiliados está en pausa: las nuevas recomendaciones no generan nada hasta que se reanude. Lo que ya has ganado es tuyo.",
    "aff.banner.accountDisabled": "Tu cuenta de afiliado está desactivada. No se contará ninguna recomendación nueva; contacta con Solunary.",

    "aff.share.title": "Tus enlaces de referido",
    "aff.share.desc": "Quien llegue por uno de estos enlaces se te atribuye durante {days} días.",
    "aff.share.signup": "Alta de profesional",
    "aff.share.marketplace": "Marketplace",
    "aff.share.copy": "Copiar",
    "aff.share.copied": "Copiado",
    "aff.share.codeLabel": "Tu código:",
    "aff.share.rates": "{saas} % de la suscripción de un profesional referido · {marketplace} % de la comisión de la plataforma en una venta referida. Tasas indicativas: cada comisión conserva la tasa vigente en el momento del cobro.",

    "aff.code.title": "Cambiar tu código",
    "aff.code.hint": "Puedes sustituir el código generado una sola vez. Los enlaces ya compartidos apuntan al código actual y dejarán de funcionar.",
    "aff.code.placeholder": "micodigo",
    "aff.code.submit": "Cambiar código",
    "aff.code.pattern": "De 4 a 16 caracteres: minúsculas y dígitos.",
    "aff.code.saved": "Código cambiado.",
    "aff.code.locked": "Tu código ya se cambió una vez: ahora es definitivo.",
    "aff.code.disabled": "El código no puede cambiarse mientras tu cuenta de afiliado esté desactivada.",

    "aff.stats.title": "Tus resultados",
    "aff.stats.clicksTotal": "Clics",
    "aff.stats.clicks30d": "{count} en los últimos 30 días",
    "aff.stats.referrals": "Profesionales referidos",
    "aff.stats.referralsBreakdown": "{linked} registrados · {active} activos · {expired} finalizados",
    "aff.stats.accrued": "Ganado (bruto)",
    "aff.stats.onHold": "En espera",
    "aff.stats.released": "Liberado",
    "aff.stats.available": "Retirable hoy",
    "aff.stats.withdrawn": "Ya pagado",
    "aff.stats.reversed": "Devuelto",
    "aff.stats.identity": "Liberado + en espera + ya pagado = ganado − devuelto. Lo retirable hoy nunca supera lo liberado ni el saldo de tu monedero.",

    "aff.payout.title": "Cómo cobras",
    "aff.payout.body": "Tus ganancias de afiliación llegan a tu monedero Solunary y se retiran en el mismo sitio que tus ventas. No hay un pago aparte.",
    "aff.payout.walletCta": "Abrir mi monedero",
    "aff.payout.notReady": "No podrás retirar nada hasta que conectes tu cuenta bancaria de cobro.",
    "aff.payout.setupCta": "Configurar mi cuenta de cobro",

    "aff.referrals.title": "Profesionales que has referido",
    "aff.referrals.empty": "Aún no hay referidos. Comparte tu enlace de alta para empezar.",
    "aff.referrals.filterAll": "Todos los estados",
    "aff.referrals.colPractice": "Consulta",
    "aff.referrals.colStatus": "Estado",
    "aff.referrals.colLinkedAt": "Alta",
    "aff.referrals.colActivatedAt": "Primera factura",
    "aff.referrals.colWindowEndsAt": "Fin del periodo",
    "aff.referrals.colInvoices": "Facturas",
    "aff.referrals.colEarned": "Ganado",

    "aff.referralState.linked": "Registrado",
    "aff.referralState.active": "Activo",
    "aff.referralState.expired": "Periodo finalizado",
    "aff.referralState.voided": "Anulado",

    "aff.commissions.title": "Comisiones",
    "aff.commissions.baseHint": "La base es la comisión que cobró la plataforma en esa venta, no el importe de la venta.",
    "aff.commissions.empty": "Aún no hay comisiones.",
    "aff.commissions.filterAllKinds": "Todos los orígenes",
    "aff.commissions.filterAllStates": "Todos los estados",
    "aff.commissions.colCreatedAt": "Fecha",
    "aff.commissions.colKind": "Origen",
    "aff.commissions.colCounterparty": "Consulta",
    "aff.commissions.colBase": "Base",
    "aff.commissions.colRate": "Tasa",
    "aff.commissions.colAmount": "Importe",
    "aff.commissions.colReversed": "Devuelto",
    "aff.commissions.colStatus": "Estado",
    "aff.commissions.colHold": "Liberado el",
    "aff.commissions.ratePct": "{pct} %",

    "aff.kind.saas": "Suscripción",
    "aff.kind.marketplace": "Venta",

    "aff.state.accrued": "Ganada",
    "aff.state.partially_reversed": "Parcialmente devuelta",
    "aff.state.reversed": "Devuelta",

    "aff.page.info": "{from}–{to} de {total}",
    "aff.page.prev": "Anterior",
    "aff.page.next": "Siguiente",

    "aff.error.AFFILIATE_PROGRAM_DISABLED": "El programa de afiliados está en pausa: esta acción no está disponible por ahora.",
    "aff.error.AFFILIATE_CODE_UNKNOWN": "No existe ninguna cuenta de afiliado con este código.",
    "aff.error.AFFILIATE_CODE_TAKEN": "Otra consulta ya tiene este código. Elige uno distinto.",
    "aff.error.AFFILIATE_CODE_INVALID": "Código no válido: de 4 a 16 caracteres, solo minúsculas y dígitos.",
    "aff.error.AFFILIATE_CODE_ALREADY_CHANGED": "Tu código ya se cambió una vez y no puede volver a cambiarse.",
    "aff.error.AFFILIATE_ACCOUNT_DISABLED": "Tu cuenta de afiliado está desactivada.",
    "aff.error.AFFILIATE_COMMISSION_NOT_FOUND": "Esta comisión ya no existe.",
    "aff.error.AFFILIATE_COMMISSION_ALREADY_REVERSED": "Esta comisión ya se ha devuelto por completo.",
    "aff.error.AFFILIATE_RULE_INVALID": "Esa regla de tasa no es válida.",
    "aff.error.REAUTH_REQUIRED": "Esta acción requiere una verificación de identidad.",
    "aff.error.RATE_LIMITED": "Demasiadas solicitudes: inténtalo de nuevo en un momento.",
    "aff.error.UNKNOWN": "Algo ha fallado y no podemos decir qué.",
  },
};
